import { createClient } from '@supabase/supabase-js';
import { Database } from '@annote/types';
import { authLogger } from '@annote/core/utils/debug';
import { SUPABASE_KEY, SUPABASE_URL } from 'src/config/supabase';

authLogger.debug('[supabase.ts] Module loading...');
authLogger.debug('[supabase.ts] SUPABASE_URL:', SUPABASE_URL ? 'set' : 'MISSING');
authLogger.debug('[supabase.ts] SUPABASE_KEY:', SUPABASE_KEY ? 'set' : 'MISSING');

// Custom storage adapter that handles Safari's localStorage restrictions
const getStorage = (): Storage => {
  authLogger.debug('[supabase.ts] getStorage() called');

  try {
    // Try localStorage first
    if (typeof window !== 'undefined' && window.localStorage) {
      // Test that localStorage actually works (Safari in private mode may have it but throw on use)
      const testKey = '__annote_storage_test__';
      window.localStorage.setItem(testKey, 'test');
      window.localStorage.removeItem(testKey);
      authLogger.debug('[supabase.ts] localStorage available and working');
      return window.localStorage;
    }
    authLogger.debug('[supabase.ts] window.localStorage not available');
  } catch (e) {
    // Safari might throw if localStorage is disabled
    authLogger.warn('[supabase.ts] localStorage test failed:', e);
  }

  // Fallback to memory storage if localStorage fails
  authLogger.debug('[supabase.ts] Using memory storage fallback');
  const memoryStorage: Record<string, string> = {};
  return {
    getItem: (key: string): string | null => memoryStorage[key] || null,
    setItem: (key: string, value: string): void => {
      memoryStorage[key] = value;
    },
    removeItem: (key: string): void => {
      delete memoryStorage[key];
    },
    get length(): number {
      return Object.keys(memoryStorage).length;
    },
    clear: (): void => {
      Object.keys(memoryStorage).forEach((key) => delete memoryStorage[key]);
    },
    key: (index: number): string | null => Object.keys(memoryStorage)[index] || null,
  };
};

authLogger.debug('[supabase.ts] Creating Supabase client...');

export const supabaseClient = createClient<Database>(SUPABASE_URL, SUPABASE_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
    storageKey: 'annote-auth',
    storage: getStorage(),
  },
});

authLogger.debug('[supabase.ts] Supabase client created successfully');
